import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Box,
  Text,
  VStack,
  Center,
  Spinner,
} from '@chakra-ui/react'
import React, { useMemo, useState, useEffect } from 'react'
import { useGetAlertsByIdsPaginatedQuery, Order_By } from '@/generated/graphql'
import { AlertTableColumn } from '@/generated/enums'
import { SpoofReportsTable } from '@/components/web2/spoof_reports_table'
import {
  spoofingReportsOrderByClause,
  SortType,
  ReportSortingMechanism,
} from '@/utils/reports/report_sorting_mechanism'
import {
  SpoofReportTableFilterProvider,
  useSelectedPage,
  useSelectedPageSize,
} from '@/hooks/spoof_report_table_filters'
import { DOPPEL_DARK, DOPPEL_LIGHT_GREY } from '../../utils/style'
import { maxPageCount } from '@/utils'
import GroupedTimeSeriesWidget from './dashboard/grouped_time_series_widget'
import { DrilldownWidgetType } from '@/utils/constants'

const VISIBLE_COLUMNS = [
  AlertTableColumn.ALERT_CREATED,
  AlertTableColumn.URL,
  AlertTableColumn.SOURCE,
  AlertTableColumn.STATE,
  AlertTableColumn.SEVERITY,
  AlertTableColumn.BRAND,
]

// Custom wrapper component for modal widgets that removes background, padding, and border
const TransparentWidgetWrapper: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return (
    <Box
      sx={{
        '& > div': {
          bg: 'transparent !important',
          padding: '0 !important',
          border: 'none !important',
        },
      }}
    >
      {children}
    </Box>
  )
}

// A modal that displays detailed analytics data for a specific widget
// when a user clicks on a widget in the analytics page.
//
// @param props - The props for the component
// @returns The AnalyticsDetailModal component

type ModalProps = {
  isOpen: boolean
  onClose: () => void
  chartData?: any
  alertData: any
  title: string
  helperText: string
  widgetType: DrilldownWidgetType
}

const AnalyticsDetailModal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  chartData,
  alertData,
  title,
  helperText,
  widgetType,
}: ModalProps) => {
  return (
    // needed for pagination
    <SpoofReportTableFilterProvider>
      <ModalInner
        alertData={alertData}
        chartData={chartData}
        helperText={helperText}
        isOpen={isOpen}
        onClose={onClose}
        title={title}
        widgetType={widgetType}
      />
    </SpoofReportTableFilterProvider>
  )
}

const ModalInner: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  chartData,
  alertData,
  title,
  helperText,
  widgetType,
}) => {
  const [selectedPage] = useSelectedPage()
  const [selectedPageSize] = useSelectedPageSize()
  const [alerts, setAlerts] = useState([])
  const [totalPageCount, setTotalPageCount] = useState(0)
  const [setToRefreshCache, setSetToRefreshCache] = useState(false)

  // sorting
  const [sortingMechanism, setSortingMechanism] = useState<ReportSortingMechanism>({
    sortingType: SortType.created_at,
    orderBy: Order_By.Desc,
  })
  const handleSortingChange = (newSortingMechanism: ReportSortingMechanism) => {
    if (JSON.stringify(newSortingMechanism) !== JSON.stringify(sortingMechanism)) {
      setSortingMechanism(newSortingMechanism)
      setSetToRefreshCache(true)
    }
  }

  // extract alert_ids from widget props if available
  const alertIds = useMemo(() => {
    let allAlertIds: string[] = []

    if (alertData?.length) {
      if (widgetType === DrilldownWidgetType.GROUPED_TIME_SERIES) {
        allAlertIds = alertData.flatMap((dataPoint: any) =>
          Array.isArray(dataPoint.alert_ids) ? dataPoint.alert_ids : [],
        )
      } else if (widgetType === DrilldownWidgetType.NUMBER) {
        allAlertIds = alertData
      }
    }

    return allAlertIds
  }, [alertData, widgetType])

  const isAlertDataAvailable = !!alertData && alertData.length > 0
  const hasAlertIds = alertIds.length > 0
  const shouldLoadData = isAlertDataAvailable && hasAlertIds

  // query for alerts by IDs if we have them
  const {
    data: alertsByIdsData,
    loading: alertsByIdsLoading,
    error: alertsByIdsError,
  } = useGetAlertsByIdsPaginatedQuery({
    variables: {
      ids: alertIds,
      includeCreditCardData: true,
      limit: selectedPageSize,
      offset: (selectedPage - 1) * selectedPageSize,
      order_by: spoofingReportsOrderByClause(null, sortingMechanism),
    },
    skip: !shouldLoadData,
  })

  useEffect(() => {
    if (alertsByIdsData?.spoofing_reports) {
      setAlerts(alertsByIdsData.spoofing_reports)
      setTotalPageCount(maxPageCount(alertIds.length, selectedPageSize))
    } else {
      setAlerts([])
      setTotalPageCount(0)
    }
  }, [alertsByIdsData, alertIds.length, selectedPageSize])

  const isLoading = alertsByIdsLoading

  return (
    <Modal isOpen={isOpen} onClose={onClose} scrollBehavior="inside" size="6xl">
      <ModalOverlay />

      <ModalContent bg={DOPPEL_DARK}>
        <ModalHeader paddingBottom={0}>{title}</ModalHeader>

        {helperText && (
          <ModalHeader color={DOPPEL_LIGHT_GREY} fontSize={18} paddingTop={0}>
            {helperText}
          </ModalHeader>
        )}

        <ModalCloseButton />

        <ModalBody>
          <VStack align="stretch" spacing={4}>
            {widgetType === DrilldownWidgetType.GROUPED_TIME_SERIES && (
              <Box>
                <TransparentWidgetWrapper>
                  <GroupedTimeSeriesWidget {...chartData} helperText="" title="" />
                </TransparentWidgetWrapper>
              </Box>
            )}

            {hasAlertIds ? (
              <Box>
                {isLoading ? (
                  <>
                    <Center p={8}>
                      <Spinner size="xl" />
                    </Center>
                  </>
                ) : alerts?.length > 0 ? (
                  <SpoofReportsTable
                    data={alerts}
                    fixedVisibleColumns={VISIBLE_COLUMNS}
                    isOrgUnifiedView={false}
                    loading={isLoading}
                    onSortingChange={handleSortingChange}
                    openInNewTab={true}
                    setToRefreshCache={setToRefreshCache}
                    sortingMechanism={sortingMechanism}
                    totalPageCount={totalPageCount}
                  />
                ) : (
                  <Center p={8}>
                    <Text>No related alerts found.</Text>
                  </Center>
                )}
              </Box>
            ) : null}
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default AnalyticsDetailModal
